import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { EndpointsGrid } from "../components";
import {
  triggerOnDemandInventorySync,
  getData,
  excludeItem,
  includeItem,
  triggerOnDemandAgentSync,
} from "../utils/apiFunctions";
import {
  OperationType,
  ExclusionType,
  SyncOperationType,
  KaseyaInventoryItemType,
  ExclusionActionType,
  TaskStatusType
} from "../utils/enums";
import {
  ApiEndpoints,
  LocationPaths,
} from "../utils/common";
import { useStateContext } from "../contexts/ContextProvider";
import { useQuery } from "@tanstack/react-query";
import { html } from "lit";

import defaultStateImg from "../assets/images/state_icons/msp-icon-ksx-default.svg";
import inSyncImg from "../assets/images/state_icons/msp-icon-ksx-sync_inv_sync_agent.svg";
import inInvSyncAgentExcImg from "../assets/images/state_icons/msp-icon-ksx-sync_inv_ex_agent.svg";
import excInvSyncAgentSyncImg from "../assets/images/state_icons/msp-icon-ksx-ex_inv_sync_agent.svg";
import excInvSyncExcAgentSyncImg from "../assets/images/state_icons/msp-icon-ksx-ex_inv_ex_agent.svg";
import notInvSyncBothExcluded from "../assets/images/state_icons/msp-icon-ksx-ex_inv_ex_agent.svg";
import notInvSyncInvExcluded from "../assets/images/state_icons/msp-icon-ksx-ex_inv_default_agent.svg";
import notInvSyncAgentExcluded from "../assets/images/state_icons/msp-icon-ksx-default_inv_ex_agent.svg";

const getSelectOptions = (node) => {
  return [
    {
      title: "Sync Inventory",
      value: "syncInventory",
      disabled: false,
    },
    {
      title: "Exclude from Inventory Sync",
      value: "handleInventorySyncExclusion",
      disabled: false,
    },
    {
      title: "Sync Agents",
      value: "syncAgents",
      disabled: false,
    },
    {
      title: "Exclude from Agents Sync",
      value: "handleAgentsSyncExclusion",
      disabled: false,
    },
  ];
};

const elementClassListToggle = (element, className) => {
  if (!element.classList.contains(className)) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
};

const handleMouseEnter = (e, node) => {
  let x = e.clientX;
  let y = e.clientY;

  const body = document.body;
  const syncInfoContainer = document.createElement("div");
  syncInfoContainer.id = "syncDetailsTooltip";
  elementClassListToggle(syncInfoContainer, "rounded-md");
  syncInfoContainer.style.position = "absolute";
  syncInfoContainer.style.left = x + 10 + "px";
  syncInfoContainer.style.top = y + 10 + "px";
  syncInfoContainer.style.backgroundColor = "#4d4d4d";
  syncInfoContainer.style.width = "200px";
  syncInfoContainer.style.height = "78px";
  syncInfoContainer.style.zIndex = "9999";

  const createContent = (node) => {
    const getStatus = (node) => {
      let syncStatus;

      for (const [key, value] of Object.entries(node)) {
        if (key === "originalData" && typeof key !== "undefined") {
          syncStatus = {
            isSynchronized: value.isSynchronized ? "Yes" : "No",
            isSyncExcluded: value.isSyncExcluded ? "Yes" : "No",
            isDeployExcluded: value.isDeployExcluded ? "Yes" : "No",
          };
        } else {
          if (key === "isSynchronized") {
            syncStatus = {
              ...syncStatus,
              isSynchronized: value ? "Yes" : "No",
            };
          }
          if (key === "isSyncExcluded") {
            syncStatus = {
              ...syncStatus,
              isSyncExcluded: value ? "Yes" : "No",
            };
          }
          if (key === "isDeployExcluded") {
            syncStatus = {
              ...syncStatus,
              isDeployExcluded: value ? "Yes" : "No",
            };
          }
        }
      }
      return syncStatus;
    };

    return `
    <div class="flex flex-col items-start">
      <div class="flex flex-row mt-2 ml-2 gap-2">
        <span class="text-white font-gzStyle text-xs/5">Has association: </span>
        <span class="text-white font-gzStyle text-xs/5">${getStatus(node).isSynchronized
      }</span>
      </div>
      <div class="flex flex-row ml-2 gap-2">
        <span class="text-white font-gzStyle text-xs/5">Inventory sync excluded: </span>
        <span class="text-white font-gzStyle text-xs/5">${getStatus(node).isSyncExcluded
      }</span>
      </div>
      <div class="flex flex-row ml-2 gap-2">
        <span class="text-white font-gzStyle text-xs/5">Agent sync excluded: </span>
        <span class="text-white font-gzStyle text-xs/5">${getStatus(node).isDeployExcluded
      }</span>
      </div>
    </div>
    `;
  };
  syncInfoContainer.insertAdjacentHTML("beforeend", createContent(node));

  body.appendChild(syncInfoContainer);
};

const handleMouseLeave = (e) => {
  const body = document.body;
  const tooltip = document.getElementById("syncDetailsTooltip");
  body.removeChild(tooltip);
};

const getInventoryTypeIcon = (entityType) => {
  if (entityType === KaseyaInventoryItemType.ORGANIZATION) {
    return "ds-icon-companies";
  }
  if (entityType === KaseyaInventoryItemType.SITE) {
    return "ds-icon-customer";
  }
  if (entityType === KaseyaInventoryItemType.GROUP) {
    return "ds-icon-group";
  }
};

const customIconRenderer = (icon, node) => {
  let iconType = node.contextTypeForIcon ?? node.originalData.contextTypeForIcon;

  return html`
    <span class="mt-1 mr-1">
      <gz-icon
        name="${getInventoryTypeIcon(iconType)}"
        size="12"
        color="var(--grey-layout-500)"
      ></gz-icon>
    </span>
    <span
      id="treeStateSpan"
      @mouseenter=${(e) => handleMouseEnter(e, node)}
      @mouseleave=${handleMouseLeave}
    >
      <img src=${icon} alt="stateIcons" />
    </span>
  `;
};

const getIconNamePerSyncStatus = (
  isSynchronized,
  isSyncExcluded,
  isDeployExcluded,
  node
) => {
  let stateIcon;
  // synchronized but not excluded - both agent and inventory
  if (isSynchronized && !isSyncExcluded && !isDeployExcluded) {
    stateIcon = inSyncImg;
  }
  //synchronized but only inventory sync excluded
  else if (isSynchronized && isSyncExcluded && !isDeployExcluded) {
    stateIcon = excInvSyncAgentSyncImg;
  }
  //synchronized but only agent sync excluded
  else if (isSynchronized && !isSyncExcluded && isDeployExcluded) {
    stateIcon = inInvSyncAgentExcImg;
  }
  //synchronized and both sync excluded
  else if (isSynchronized && isSyncExcluded && isDeployExcluded) {
    stateIcon = excInvSyncExcAgentSyncImg;
  }
  //not synchronized but only inventory sync excluded
  else if (!isSynchronized && isSyncExcluded && !isDeployExcluded) {
    stateIcon = notInvSyncInvExcluded;
  }
  //not synchronized but only agent sync excluded
  else if (!isSynchronized && !isSyncExcluded && isDeployExcluded) {
    stateIcon = notInvSyncAgentExcluded;
  }
  //not synchronized and both sync excluded
  else if (!isSynchronized && isSyncExcluded && isDeployExcluded) {
    stateIcon = notInvSyncBothExcluded;
  } else {
    // is not synchronized and not sync excluded and not deploy excluded - default state
    stateIcon = defaultStateImg;
  }

  return customIconRenderer(stateIcon, node);
};

let selectedNode;
let inventoryTreeRef;
let triggeredOndemandInventoryTasks = [];

const InventoryMappings = () => {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["fetchOrgs"],
    queryFn: () => getData(`${ApiEndpoints.INVENTORY_ORGS}`),
  });
  const [selectedNodeElement, setSelectedNodeElement] = useState();
  const { setLocation, finishedOnDemandInventoryTask } = useStateContext();
  const locationData = useLocation();
  const queryClient = useQueryClient();
  const [shouldInvalidateQueries, setShouldInvalidateQueries] = useState(false);
  const { isRecurrentInventoryTaskRunning, isOndemandInventoryTaskRunning, isOndemandAgentTaskRunning, isRecurrentAgentTaskRunning } = useStateContext();
  let isInventoryTaskRunning = isOndemandInventoryTaskRunning || isRecurrentInventoryTaskRunning
  let isAgentTaskRunning = isOndemandAgentTaskRunning || isRecurrentAgentTaskRunning

  const handleNodeSelection = (e) => {
    e.stopPropagation();
    selectedNode = e.target.__node;
    setSelectedNodeElement(selectedNode);
  };

  const handleNodeStateUpdate = async (endpoint, nodeId) => {
    let filteredNodeDataList;

    if (endpoint === ApiEndpoints.INVENTORY_GROUPS) {
      filteredNodeDataList = await getData(
        endpoint,
        `filter=Id eq ${nodeId.replace("groupId_", "")}`
      );
    } else {
      filteredNodeDataList = await getData(endpoint, `filter=Id eq ${nodeId}`);
    }

    let parentNodeId = filteredNodeDataList[0].type === KaseyaInventoryItemType.SITE ? filteredNodeDataList[0].parentOrganizationId :
      (filteredNodeDataList[0].type === KaseyaInventoryItemType.GROUP ? filteredNodeDataList[0].parentSiteId : null)

    let updatedNodeData = {
      nodeId:
        filteredNodeDataList[0].type === KaseyaInventoryItemType.GROUP
          ? `groupId_${nodeId}`
          : nodeId,
      nodeName: filteredNodeDataList[0].name,
      parentNodeId: parentNodeId,
      namePrefixRenderer: () =>
        getIconNamePerSyncStatus(
          filteredNodeDataList[0].isSynchronized,
          filteredNodeDataList[0].isSyncExcluded,
          filteredNodeDataList[0].isDeployExcluded,
          filteredNodeDataList[0]
        ),
      originalData: {
        type: filteredNodeDataList[0].type,
        isSynchronized: filteredNodeDataList[0].isSynchronized,
        isSyncExcluded: filteredNodeDataList[0].isSyncExcluded,
        isDeployExcluded: filteredNodeDataList[0].isDeployExcluded,
        inventorySyncExclusionId:
          filteredNodeDataList[0].inventorySyncExclusionId,
        agentSyncExclusionId: filteredNodeDataList[0].agentSyncExclusionId,
        contextTypeForIcon: filteredNodeDataList[0].contextTypeForIcon
      },
    };

    inventoryTreeRef.dataSource.setNode(updatedNodeData);
    setShouldInvalidateQueries(true);
  };

  const triggerExcludeAction = async (
    nodeId,
    nodeName,
    nodeType,
    exclusionType,
    exclusionId,
    action
  ) => {

    if (action === ExclusionActionType.EXCLUDE) {
      let itemToExclude = {
        ItemId: nodeId,
        ContextType: nodeType,
        ExclusionType: exclusionType
      }

      await excludeItem(itemToExclude, nodeName)
    }

    if (action === ExclusionActionType.INCLUDE) {
      let itemToInclude = {
        ExclusionId: exclusionId,
        ContextType: nodeType,
        ExclusionType: exclusionType
      }

      await includeItem(itemToInclude, nodeName)
    }

    let url;
    switch (nodeType) {
      case KaseyaInventoryItemType.ORGANIZATION:
        url = ApiEndpoints.INVENTORY_ORGS
        break
      case KaseyaInventoryItemType.SITE:
        url = ApiEndpoints.INVENTORY_SITES
        break
      case KaseyaInventoryItemType.GROUP:
        url = ApiEndpoints.INVENTORY_GROUPS
        break
    }

    await handleNodeStateUpdate(url, nodeId);
  };

  const handleExclusionActions = async (
    nodeId,
    nodeName,
    nodeOriginalData,
    syncOperationType
  ) => {
    //handle inventory sync exclusion action and no existing exclusion ID exists for the target node -> exclude action
    if (
      syncOperationType === SyncOperationType.InventorySyncExclusion &&
      nodeOriginalData.inventorySyncExclusionId === null
    ) {

      await triggerExcludeAction(
        nodeId,
        nodeName,
        nodeOriginalData.type,
        ExclusionType.InventorySync,
        nodeOriginalData.inventorySyncExclusionId,
        ExclusionActionType.EXCLUDE
      );
    }
    //handle agent sync exclusion action and no existing exclusion ID exists for the target node -> exclude action
    else if (
      syncOperationType === SyncOperationType.SyncAgentsExclusion &&
      nodeOriginalData.agentSyncExclusionId === null
    ) {
      await triggerExcludeAction(
        nodeId,
        nodeName,
        nodeOriginalData.type,
        ExclusionType.EndpointsSync,
        nodeOriginalData.agentSyncExclusionId,
        ExclusionActionType.EXCLUDE
      );
    }
    //handle inventory sync exclusion action and an existing exclusion ID exists for the target node -> include action
    else if (
      syncOperationType === SyncOperationType.InventorySyncExclusion &&
      typeof nodeOriginalData.inventorySyncExclusionId !== "undefined"
    ) {
      await triggerExcludeAction(
        nodeId,
        nodeName,
        nodeOriginalData.type,
        ExclusionType.InventorySync,
        nodeOriginalData.inventorySyncExclusionId,
        ExclusionActionType.INCLUDE,
      );
    }
    //handle agent sync exclusion action and an existing exclusion ID exists for the target node -> include action
    else if (
      syncOperationType === SyncOperationType.SyncAgentsExclusion &&
      typeof nodeOriginalData.agentSyncExclusionId !== "undefined"
    ) {
      await triggerExcludeAction(
        nodeId,
        nodeName,
        nodeOriginalData.type,
        ExclusionType.EndpointsSync,
        nodeOriginalData.agentSyncExclusionId,
        ExclusionActionType.INCLUDE,
      );
    }
  };

  const handleInventorySelectionClick = async (event, nodeElement) => {
    let targetedNodeId =
      nodeElement.node.originalData.type === KaseyaInventoryItemType.GROUP
        ? nodeElement.node.nodeId.replace("groupId_", "")
        : nodeElement.node.nodeId;

    if (event.detail.selected === SyncOperationType.InventorySync) {
      let inventoryOnDemandSyncDto =
        [{
          itemId: targetedNodeId,
          operationType: OperationType.SYNC,
          contextType: nodeElement.node.originalData.type
        }]

      let taskId = await triggerOnDemandInventorySync(inventoryOnDemandSyncDto);
      triggeredOndemandInventoryTasks.push({
        nodeId: targetedNodeId,
        taskIdentifier: taskId,
        nodeType: nodeElement.node.originalData.type,
      });
    }

    if (
      event.detail.selected === SyncOperationType.InventorySyncExclusion ||
      event.detail.selected === SyncOperationType.SyncAgentsExclusion
    ) {
      await handleExclusionActions(
        targetedNodeId,
        nodeElement.node.nodeName,
        nodeElement.node.originalData,
        event.detail.selected
      );
    }

    if (event.detail.selected === SyncOperationType.SyncAgents) {
      if (nodeElement.node.originalData.type === KaseyaInventoryItemType.GROUP) {
        const runSynchronizeEndpointsTaskOnDemand = [{
          ContextType: nodeElement.node.originalData.type,
          ItemId: targetedNodeId,
        }]

        await triggerOnDemandAgentSync(runSynchronizeEndpointsTaskOnDemand);
      } else {
        await handleSyncAgents(targetedNodeId, nodeElement.node.originalData.type)
      }
    }
  };

  const handleSyncAgents = async (targetedNodeId, type) => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');

    footer.innerHTML = `
      <gz-button id="syncAgents">Sync Agents</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = `
      <p style="margin-bottom: 1rem;">You are about to start synchronizing the Bitdefender agent on the selected targets. During this process, the agent may be installed or reinstalled as needed.<br>
      This task will only affect synchronized inventories.</p>
    `;

    const syncAgentsButton = footer.querySelector('#syncAgents');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: 'Synchronize agents?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (syncAgentsButton) {
      syncAgentsButton.addEventListener('click', async () => {
        dialog.dismiss();
        const runSynchronizeEndpointsTaskOnDemand = [{
          ContextType: type,
          ItemId: targetedNodeId,
        }]

        await triggerOnDemandAgentSync(runSynchronizeEndpointsTaskOnDemand);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss(); });
    }
  }

  const handleInventoryContextClick = (event, nodeElement) => {
    elementClassListToggle(event.target, "active");
    let select = event.target.parentElement;

    if (isInventoryTaskRunning) {
      let index = select.__options.findIndex((obj) => obj.value === "syncInventory");
      select.__options[index].disabled = true
    }

    if (isAgentTaskRunning || !nodeElement.originalData.isSynchronized) {
      let index = select.__options.findIndex((obj) => obj.value === "syncAgents");
      select.__options[index].disabled = true
    }

    if (nodeElement.originalData.isParentSynchronized !== undefined && !nodeElement.originalData.isParentSynchronized) {
      let index = select.__options.findIndex((obj) => obj.value === "syncInventory");
      select.__options[index].disabled = true
    }

    if (nodeElement.originalData.isSyncExcluded) {
      let index = select.__options.findIndex(
        (obj) => obj.value === "handleInventorySyncExclusion"
      );
      select.__options[index].title = "Include in Inventory Sync";
    }
    if (nodeElement.originalData.isDeployExcluded) {
      let index = select.__options.findIndex(
        (obj) => obj.value === "handleAgentsSyncExclusion"
      );
      select.__options[index].title = "Include in Agents Sync";
    }
  };

  const getNodeActionsElement = (nodeElement) => {
    const selectOptions = getSelectOptions(nodeElement);

    const iconButton = document.createElement("gz-icon-button");
    iconButton.icon = "ds-icon-more";
    iconButton.fontSize = "24";
    iconButton.buttonType = "square";
    iconButton.buttonFill = "quiet";
    iconButton.buttonColor = "secondary";
    iconButton.addEventListener("mousedown", (event) =>
      handleInventoryContextClick(event, nodeElement.__node)
    );

    const select = document.createElement("gz-select");
    elementClassListToggle(select, "treeSelectElement");
    select.overlayPositionX = "left";
    select.options = selectOptions;
    select.addEventListener("selectionChanged", (event) =>
      handleInventorySelectionClick(event, nodeElement)
    );

    select.appendChild(iconButton);

    return select;
  };

  const nodeRenderer = (defaultNodeRenderer, nodeElement) => {
    nodeElement.addEventListener("nodeClicked", handleNodeSelection);
    const span = document.createElement("span");
    elementClassListToggle(span, "node-action");
    span.addEventListener("click", (event) => event.stopPropagation());
    span.appendChild(getNodeActionsElement(nodeElement));
    return defaultNodeRenderer(span);
  };

  const getRootNodeData = async (nodeId) => {
    let nodeData;

    data.forEach((org) => {
      if (Number.parseInt(org.id) === Number.parseInt(nodeId)) {
        nodeData = {
          currentNode: {
            nodeId: nodeId === "undefined" ? ["kaseya"] : nodeId,
            nodeName: org.name,
            namePrefixRenderer: () =>
              getIconNamePerSyncStatus(
                org.isSynchronized,
                org.isSyncExcluded,
                org.isDeployExcluded,
                org
              ),
            originalData: {
              type: org.type,
              isSynchronized: org.isSynchronized,
              isSyncExcluded: org.isSyncExcluded,
              isDeployExcluded: org.isDeployExcluded,
              inventorySyncExclusionId: org.inventorySyncExclusionId,
              agentSyncExclusionId: org.agentSyncExclusionId,
              contextTypeForIcon: org.contextTypeForIcon
            },
          },
          childNodes: [],
        };
      }
    });

    return Promise.resolve(nodeData);
  };

  const getChildNodeData = async (nodeElement) => {
    let childItems = [];

    if (
      nodeElement.originalData.type === KaseyaInventoryItemType.ORGANIZATION
    ) {
      childItems = await getData(
        `${ApiEndpoints.INVENTORY_SITES}`,
        `filter=ParentId eq ${nodeElement.nodeId}`
      );
    }

    if (nodeElement.originalData.type === KaseyaInventoryItemType.SITE) {
      childItems = await getData(
        `${ApiEndpoints.INVENTORY_GROUPS}`,
        `filter=ParentSiteId eq ${nodeElement.nodeId}`
      );
    }

    let currentNode = {
      currentNode: {
        nodeId: nodeElement.nodeId,
        nodeName: nodeElement.nodeName,
        icon: getInventoryTypeIcon(nodeElement.originalData.contextTypeForIcon),
        namePrefixRenderer: () =>
          getIconNamePerSyncStatus(
            nodeElement.originalData.isSynchronized,
            nodeElement.originalData.isSyncExcluded,
            nodeElement.originalData.isDeployExcluded,
            nodeElement
          ),
        originalData: nodeElement.originalData,
        leaf: nodeElement.leaf,
      },
      childNodes:
        childItems &&
        childItems.map((x) => {
          return {
            nodeId:
              nodeElement.originalData.type === KaseyaInventoryItemType.SITE
                ? `groupId_${x["id"]}`
                : x["id"],
            nodeName: x["name"],
            namePrefixRenderer: () =>
              getIconNamePerSyncStatus(
                x.isSynchronized,
                x.isSyncExcluded,
                x.isDeployExcluded,
                x
              ),
            originalData: {
              type: x["type"],
              isSynchronized: x["isSynchronized"],
              isSyncExcluded: x["isSyncExcluded"],
              isDeployExcluded: x["isDeployExcluded"],
              inventorySyncExclusionId: x["inventorySyncExclusionId"],
              agentSyncExclusionId: x["agentSyncExclusionId"],
              contextTypeForIcon: x["contextTypeForIcon"],
              isParentSynchronized: nodeElement.originalData.isSynchronized
            },
            leaf:
              nodeElement.originalData.type === KaseyaInventoryItemType.SITE,
          };
        }),
    };

    return Promise.resolve(currentNode);
  };

  let dynamicDataSourceOptions = {
    rootNodeIds: isSuccess ? data.map((org) => org["id"]) : ["kaseya"],
    dragAndDropEnabled: false,
    requestNodeData: getChildNodeData,
    requestRootNodeData: getRootNodeData,
  };

  useEffect(() => {
    if (locationData.pathname === LocationPaths.INVENTORY) {
      setLocation("Bitdefender / Operations / Inventory");
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      const element = document.getElementById("inventoryTreeId");
      window.globalThis.nirvana.dependencies.services.buildTreeElement(
        element,
        dynamicDataSourceOptions,
        "dynamic"
      );

      buildInventoryTree()
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      buildInventoryTree()
    }
  }, [isInventoryTaskRunning, isAgentTaskRunning])

  const buildInventoryTree = () => {
    const tree = document.getElementsByTagName("gz-tree")[0];
    tree.nodeRenderer = nodeRenderer;
    inventoryTreeRef = tree;
  }

  useEffect(() => {
    const updatedNodeAfterInventorySync = async () => {
      if (task.nodeType === KaseyaInventoryItemType.ORGANIZATION) {
        await handleNodeStateUpdate(
          `${ApiEndpoints.INVENTORY_ORGS}`,
          task.nodeId
        );
      } else if (task.nodeType === KaseyaInventoryItemType.SITE) {
        await handleNodeStateUpdate(
          `${ApiEndpoints.INVENTORY_SITES}`,
          task.nodeId
        );
      } else if (task.nodeType === KaseyaInventoryItemType.GROUP) {
        await handleNodeStateUpdate(
          `${ApiEndpoints.INVENTORY_GROUPS}`,
          task.nodeId
        );
      }

      triggeredOndemandInventoryTasks = triggeredOndemandInventoryTasks.filter(
        (t) => t.taskIdentifier !== finishedOnDemandInventoryTask.taskIdentifier
      );
    };

    if (finishedOnDemandInventoryTask?.taskStatusType === TaskStatusType.SUCCEEDED) {
      queryClient.invalidateQueries({
        queryKey: ["inventoryWithAssociations"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fetchOrgs"],
      });
    }

    let task = triggeredOndemandInventoryTasks.filter(
      (t) => t.taskIdentifier === finishedOnDemandInventoryTask.taskIdentifier
    )[0];
    if (typeof task !== "undefined") {
      updatedNodeAfterInventorySync();
    }
  }, [finishedOnDemandInventoryTask]);

  useEffect(() => {
    if (shouldInvalidateQueries) {
      queryClient.invalidateQueries({
        queryKey: ["inventoryWithAssociations"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fetchOrgs"],
      });
      setShouldInvalidateQueries(false)
    }

  }, [shouldInvalidateQueries])

  return (
    <>
      {isLoading && (
        <div className="flex flex-row h-screen items-center justify-center">
          <div className="h-[74px] w-[74px]">
            <gz-progress-spinner spinnerWidth="large" indeterminate />
          </div>
        </div>
      )}
      {isSuccess && (
        <div className="h-full bg-white ml-4 overflow-hidden">
          <gz-push-body-layout resizable="right" style={{ height: "90vh" }}>
            <div slot="push-body-sidenav">
              <div className="inventoryTree" id="inventoryTreeId" />
            </div>
            <div slot="push-body-sidenav-title">
              <gz-title id="smartViewsTitle" fontweight="600" fontsize="12">
                ORGANIZATIONS
              </gz-title>
            </div>

            <div className="alerts push-body" slot="push-body-content">
              <div className="ml-8">
                <gz-title
                  fontSize="20"
                  fontWeight="500"
                  style={{ opacity: "0.7" }}
                >
                  Inventory
                </gz-title>
              </div>
              <EndpointsGrid selectedInventoryNode={selectedNode} />
            </div>
          </gz-push-body-layout>
        </div>
      )}
    </>
  );
};

export default InventoryMappings;
